<!-- Toolbar actions are configured declaratively in the component via
     PageHeaderService.setPageActions(...). See ngAfterViewInit / product
     load handler. -->

@if (product) {
  <div>
    <!-- Shared: barcode + data sync. Rendered inside the detail card on desktop,
         and moved below the tab content on mobile (less important than the tabs). -->
    <ng-template #detailExtras>
      <div class="divide-y divide-base-300">
        @if (product?.codes?.length > 0) {
          <div class="p-4">
            <div class="flex items-center justify-between mb-2">
              <span class="text-xs font-semibold uppercase tracking-wide text-base-content/50">Barcode</span>
              @if ((product?.codes?.length ?? 0) > 1) {
                <span class="text-xs text-base-content/40">{{ product.codes.length }} codes</span>
              }
            </div>
            <button
              type="button"
              class="w-full flex items-center gap-3 px-3 py-2 rounded-lg border border-base-300 bg-base-200 hover:border-primary transition-colors"
              (click)="openCodesModal()">
              <img
                class="block shrink-0 rounded"
                [src]="getBarcodeUrl(primaryCode?.code, 160, 40) | async"
                alt=""
                [style]="loadedCodes.has(primaryCode?.code) ? 'height: 36px;' : 'width: 0px;'"
                (load)="onImageLoad(primaryCode?.code)"
                (error)="onImageError(primaryCode?.code)"/>
              <span class="font-mono text-xs text-base-content/70 truncate">{{ primaryCode?.code }}</span>
              <span class="ml-auto shrink-0 text-xs text-base-content/40">
                @if ((product?.codes?.length ?? 0) > 1) {
                  +{{ product.codes.length - 1 }}
                }
                <span class="ml-1 text-base leading-none">&rsaquo;</span>
              </span>
            </button>
          </div>
        }

        @if (productSyncStatus; as s) {
          <div class="p-4">
            <div class="flex items-center justify-between mb-1">
              <span class="text-xs font-semibold uppercase tracking-wide text-base-content/50">Data sync</span>
              <button
                type="button"
                class="btn btn-ghost btn-xs"
                [disabled]="isSyncing"
                (click)="triggerSync($event)"
                title="Trigger sync">
                <fa-icon [icon]="faSync" [class.animate-spin]="isSyncing"></fa-icon>
              </button>
            </div>
            <button
              type="button"
              class="w-full flex items-center gap-2 text-left rounded-lg -mx-1 px-1 py-1 hover:bg-base-200 transition-colors"
              (click)="toggleAllProviders($event)">
              <span class="w-2 h-2 rounded-full shrink-0" [ngClass]="syncDotClass(s.status)"></span>
              <span class="text-sm font-semibold">{{ syncStatusLabel(s.status) }}</span>
              @if (maxLastSynced(s.providers); as t) {
                <span class="text-xs text-base-content/50">· {{ t | date:'d. MMM H:mm' }}</span>
              }
              <span class="ml-auto shrink-0 text-base leading-none text-base-content/40">&rsaquo;</span>
            </button>
          </div>
        }
      </div>
    </ng-template>

    <div class="grid grid-cols-1 lg:grid-cols-12 gap-8">

      <!-- Details Column: grouped card (left on desktop, first on mobile) -->
      <div class="lg:col-span-4 2xl:col-span-3">
        <ui-card [padding]="'none'" [shadow]="false" [bordered]="true" className="overflow-hidden">

          <!-- Product image / states -->
          @if (isMeal()) {
            <!-- Meals show their source recipe's title image, view-only.
                 It's managed on the recipe, not here. -->
            @if (product.primary_full_image && !productImageLoadError) {
              <div class="aspect-[4/3] overflow-hidden rounded-box m-4">
                <img
                  [src]="product.primary_full_image?.url"
                  (load)="onProductImageLoad()"
                  (error)="onProductImageError()"
                  class="block w-full h-full object-cover"
                  alt="" />
              </div>
            } @else {
              <div class="aspect-[4/3] bg-base-200 flex items-center justify-center text-center p-6 rounded-box m-4">
                <div>
                  <div class="w-16 h-16 rounded-full bg-primary/10 text-primary flex items-center justify-center mx-auto mb-3">
                    <fa-icon [icon]="faImage" class="text-2xl"></fa-icon>
                  </div>
                  <div class="font-semibold text-base-content/70 mb-1">No image</div>
                  <div class="text-sm text-base-content/50">The image comes from the linked recipe</div>
                </div>
              </div>
            }
          } @else if (product.primary_full_image && !productImageLoadError) {
            <div
              class="relative group cursor-pointer aspect-[4/3] bg-white overflow-hidden"
              (click)="onImageEditClick()">
              <img
                [src]="product.primary_full_image?.url"
                (load)="onProductImageLoad()"
                (error)="onProductImageError()"
                class="block w-full h-full object-contain p-4"
                alt="" />

              <!-- Desktop hover overlay -->
              <div
                class="hidden lg:flex absolute inset-0 items-center justify-center bg-base-100/60 opacity-0 group-hover:opacity-100 transition-opacity">
                <fa-icon [icon]="faEdit" class="text-2xl text-primary"></fa-icon>
              </div>

              <!-- Mobile edit button -->
              <div class="lg:hidden absolute bottom-3 right-3 btn btn-primary btn-sm">
                <fa-icon [icon]="faEdit"></fa-icon>
                <span class="ml-1">Edit Image</span>
              </div>
            </div>
          } @else if (productImageLoadError) {
            <!-- Image failed to load -->
            <div
              class="aspect-[4/3] bg-error/5 flex flex-col items-center justify-center text-center p-6 cursor-pointer active:scale-[0.99] transition-transform"
              (click)="onImageEditClick()">
              <div class="w-14 h-14 rounded-full bg-error/10 text-error flex items-center justify-center mb-3">
                <fa-icon [icon]="faImage" class="text-2xl"></fa-icon>
              </div>
              <div class="font-semibold text-error mb-1">Image could not be loaded</div>
              <div class="text-sm text-base-content/60 mb-3">Tap to change the product image</div>
              <div class="flex gap-2 justify-center flex-wrap">
                <ui-button
                  [variant]="'danger'"
                  [tone]="'outline'"
                  [size]="'sm'"
                  (click)="$event.stopPropagation(); onImageEditClick()">
                  Change image
                </ui-button>
                <ui-button
                  [variant]="'secondary'"
                  [tone]="'outline'"
                  [size]="'sm'"
                  (click)="$event.stopPropagation(); onImageSearchClick()">
                  <fa-icon [icon]="faSearch" class="mr-1"></fa-icon>
                  Search Image
                </ui-button>
              </div>
            </div>
          } @else {
            <!-- No image yet -->
            <div
              class="aspect-[4/3] bg-base-200 flex items-center justify-center text-center p-6 cursor-pointer active:scale-[0.99] transition-transform"
              (click)="onImageEditClick()">
              <div>
                <div class="w-16 h-16 rounded-full bg-primary/10 text-primary flex items-center justify-center mx-auto mb-3">
                  <fa-icon [icon]="faBarcode" class="text-2xl"></fa-icon>
                </div>
                <div class="font-semibold text-base-content/70 mb-1">No image</div>
                <div class="text-sm text-base-content/50 mb-3">Tap to add a product image</div>
                <div class="flex gap-2 justify-center flex-wrap">
                  <ui-button
                    [variant]="'primary'"
                    [tone]="'outline'"
                    [size]="'sm'"
                    (click)="$event.stopPropagation(); onImageEditClick()">
                    Add Image
                  </ui-button>
                  <ui-button
                    [variant]="'secondary'"
                    [tone]="'outline'"
                    [size]="'sm'"
                    (click)="$event.stopPropagation(); onImageSearchClick()">
                    <fa-icon [icon]="faSearch" class="mr-1"></fa-icon>
                    Search Image
                  </ui-button>
                </div>
              </div>
            </div>
          }

          <!-- Self-cooked meal banner: shown when this product was created from
               a recipe's leftovers. Links back to the recipe when still linked. -->
          @if (isMeal()) {
            <div class="mx-4 my-3 flex items-start gap-3 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2.5">
              <fa-icon [icon]="faUtensils" class="text-primary mt-0.5"></fa-icon>
              <div class="text-sm">
                <div class="font-medium">Self-cooked meal</div>
                @if (product.recipe) {
                  <div class="text-base-content/70">
                    Based on
                    <button type="button" class="link link-primary"
                            (click)="openRecipe(product.recipe.id)">{{ product.recipe.title }}</button>
                  </div>
                } @else {
                  <div class="text-base-content/60">Made from a recipe's leftovers</div>
                }
              </div>
            </div>
          }

          <!-- Facts: quiet definition list -->
          <dl class="border-t border-base-300 divide-y divide-base-300">
            <div class="flex items-baseline justify-between gap-3 px-4 py-2.5">
              <dt class="text-sm text-base-content/60 shrink-0">Quantity</dt>
              <dd class="text-sm font-medium text-right flex items-center justify-end gap-1.5 flex-wrap">
                @if (product.quantity_type !== 'variable' || (product.quantity?.value && +product.quantity.value !== 0)) {
                  <span>{{ product.quantity_as_text }}</span>
                }
                @if (product.quantity_type === 'variable') {
                  <ui-badge variant="warning" size="sm" [outline]="true">Variable</ui-badge>
                }
              </dd>
            </div>

            @if (product.brands?.length > 0) {
              <div class="flex items-baseline justify-between gap-3 px-4 py-2.5">
                <dt class="text-sm text-base-content/60 shrink-0">Brand</dt>
                <dd class="text-sm font-medium text-right">{{ product.brands.join(', ') }}</dd>
              </div>
            }

            @if (product.classification?.category) {
              <div class="flex items-baseline justify-between gap-3 px-4 py-2.5">
                <dt class="text-sm text-base-content/60 shrink-0">Category</dt>
                <dd class="text-sm font-medium text-right">
                  {{ product.classification.category.name_de || product.classification.category.name_en || product.classification.category.key }}
                </dd>
              </div>
            }

            @if (product.classification?.product_kind) {
              <div class="flex items-baseline justify-between gap-3 px-4 py-2.5">
                <dt class="text-sm text-base-content/60 shrink-0">Product kind</dt>
                <dd class="text-sm font-medium text-right">
                  {{ product.classification.product_kind.default_name || product.classification.product_kind.key }}
                </dd>
              </div>
            }
          </dl>

          <!-- Add to shopping list -->
          @if (activeShoppingList) {
            @if (productListPresence?.on_list) {
              <!-- Already on the list — show a compact hint instead of the add button. -->
              <div class="flex items-center justify-center gap-2 border-t border-base-300 px-4 py-2.5 text-sm text-base-content/60 text-center">
                <fa-icon [icon]="faCartShopping" class="shrink-0 text-primary"></fa-icon>
                <span>
                  Already on {{ productListPresence.list_name ?? 'your shopping list' }}
                  @if (productListPresence.item?.subtitle) {
                    <span class="text-base-content/50">· {{ productListPresence.item.subtitle }}</span>
                  }
                </span>
              </div>
            } @else {
              <div class="border-t border-base-300 p-4">
                <ui-button variant="secondary" [fullWidth]="true" (clicked)="addToShoppingList()">
                  <fa-icon [icon]="faCartShopping" class="mr-2"></fa-icon>
                  Add to {{ activeShoppingList.name }}
                </ui-button>
              </div>
            }
          }

          <!-- Barcode + Data sync — in-card on desktop only (moved below tabs on mobile) -->
          @if (product?.codes?.length > 0 || productSyncStatus) {
            <div class="hidden lg:block border-t border-base-300">
              <ng-container [ngTemplateOutlet]="detailExtras"></ng-container>
            </div>
          }
        </ui-card>
      </div>
      <!-- Main Column: Tabs (right on desktop) -->
      <div class="lg:col-span-8 2xl:col-span-9">
        <!-- Tabs -->
        <ui-tabs
          [tabs]="productTabs"
          [activeTabId]="activeTab"
          (tabChange)="onTabChange($event)">
          <div>
            @switch (activeTab) {
              <!-- Stock Tab Content -->
              @case ('stock') {
                <div class="tab-panel">
                  @if (household && product) {
                    <div class="mb-4">
                      <app-desired-stock
                        [householdId]="household.id"
                        [productId]="product.id"
                        [canAddToList]="!!activeShoppingList"
                        (addToList)="addToShoppingList($event)"
                        [updateTrigger]="addProductSubject">
                      </app-desired-stock>
                    </div>
                  }
                  @for (store of stores; track store) {
                    <div class="mb-6 last:mb-0">
                      <div class="flex justify-between items-center">
                        <h6 class="text-sm font-semibold uppercase tracking-wide text-base-content/70">
                          {{ store.name }}
                        </h6>
                        @if (product.quantity_type === 'variable') {
                          <!-- Desktop (sm+): dropdown popover -->
                          <div class="dropdown dropdown-end hidden sm:block" [class.dropdown-open]="activeAddStore === store.id">
                            <ui-button variant="primary" tone="link" size="sm" (click)="onAddClick(product.id, store.id)">
                              <fa-icon [icon]="faAdd"></fa-icon>
                              <span class="ml-1">Add</span>
                            </ui-button>
                            @if (activeAddStore === store.id) {
                              <div class="dropdown-content z-10 mt-2 p-4 shadow-lg bg-base-100 border border-base-300 rounded-box w-72">
                                <p class="text-sm font-medium mb-3">
                                  How much? <span class="text-base-content/50">({{ product.quantity?.unit }})</span>
                                </p>
                                <div class="flex gap-2 items-center">
                                  <input
                                    class="input input-bordered input-sm flex-1"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    placeholder="{{ product.quantity?.value || '0.00' }}"
                                    [value]="variableItemQuantity"
                                    (input)="variableItemQuantity = $any($event.target).value"
                                    (keydown.enter)="confirmVariableAdd(product.id, store.id)"
                                    autofocus
                                  />
                                  <ui-button variant="primary" size="sm" (clicked)="confirmVariableAdd(product.id, store.id)">
                                    Add
                                  </ui-button>
                                  <ui-button variant="ghost" size="sm" (clicked)="activeAddStore = null">
                                    ✕
                                  </ui-button>
                                </div>
                                @if (product.quantity?.value) {
                                  <p class="text-xs text-base-content/50 mt-2">Default: {{ product.quantity?.value }} {{ product.quantity?.unit }}</p>
                                }
                              </div>
                            }
                          </div>

                          <!-- Mobile (<sm): plain button + full-screen dialog -->
                          <div class="sm:hidden">
                            <ui-button variant="primary" tone="link" size="sm" (click)="onAddClick(product.id, store.id)">
                              <fa-icon [icon]="faAdd"></fa-icon>
                              <span class="ml-1">Add</span>
                            </ui-button>
                          </div>

                          <dialog
                            class="modal sm:hidden"
                            [class.modal-open]="activeAddStore === store.id">
                            <div class="modal-box w-full h-dvh max-h-dvh max-w-none rounded-none flex flex-col">
                              <div class="flex items-center justify-between mb-6">
                                <h3 class="text-lg font-bold">Add to {{ store.name }}</h3>
                                <button class="btn btn-ghost btn-sm btn-circle" type="button" (click)="activeAddStore = null">✕</button>
                              </div>

                              <p class="text-base-content/60 mb-2">
                                How much are you adding?
                              </p>

                              <div class="join w-full mt-1">
                                <input
                                  class="input input-bordered join-item flex-1 text-xl text-center"
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  placeholder="{{ product.quantity?.value || '0.00' }}"
                                  [value]="variableItemQuantity"
                                  (input)="variableItemQuantity = $any($event.target).value"
                                  (keydown.enter)="confirmVariableAdd(product.id, store.id)"
                                />
                                @if (product.quantity?.unit) {
                                  <span class="btn btn-disabled join-item">{{ product.quantity?.unit }}</span>
                                }
                              </div>

                              @if (product.quantity?.value) {
                                <p class="text-sm text-base-content/40 mt-2">Default: {{ product.quantity?.value }} {{ product.quantity?.unit }}</p>
                              }

                              <div class="flex-1"></div>

                              <div class="flex gap-3 mt-6">
                                <ui-button variant="ghost" [fullWidth]="true" (clicked)="activeAddStore = null">Cancel</ui-button>
                                <ui-button variant="primary" [fullWidth]="true" (clicked)="confirmVariableAdd(product.id, store.id)">
                                  Add to Stock
                                </ui-button>
                              </div>
                            </div>
                          </dialog>
                        } @else {
                          <ui-button
                            variant="primary" tone="link" size="sm"
                            (click)="addProductToStock(product.id, store.id)">
                            <fa-icon [icon]="faAdd"></fa-icon>
                            <span class="ml-1">Add</span>
                          </ui-button>
                        }
                      </div>
                      <div
                        class="relative mt-3 min-h-[3.25rem] border border-base-300 rounded-lg overflow-hidden bg-base-100 transition-colors"
                        [ngClass]="{
    'opacity-95': isDragActive,
    'border-primary bg-primary/5': isDragActive && store.id !== draggedFromStoreId
  }"
                        (dragover)="isDragActive ? $event.preventDefault() : null"
                        (drop)="onDropOnStore(store.id)">
                        <!-- Overlay nur wenn drop-target -->
                        @if (isDragActive && store.id !== draggedFromStoreId) {
                          <div
                            class="absolute inset-0 flex items-center justify-center
         bg-primary/10 text-base-content text-sm font-semibold
         pointer-events-none"
                          >
                            Drop here to move item
                          </div>
                        }
                        <div
                          class="transition-all duration-150"
                          [ngClass]="{
      'blur-sm opacity-80': isDragActive && store.id !== draggedFromStoreId
    }"
                        >
                          @if (store) {
                            <app-stock-items
                              [storeId]="store.id"
                              [storeDefaultZone]="store.temperature_zone ?? null"
                              [productId]="product.id"
                              [quantityType]="product.quantity_type || 'fixed'"
                              (itemsChanged)="onStockChange()"
                              [updateTrigger]="addProductSubject"
                              (dragStart)="onStockItemDragStart($event)"
                              (dragEnd)="onStockItemDragEnd()">
                            </app-stock-items>
                          }
                        </div>
                      </div>
                    </div>
                  }
                </div>
              }
              <!-- Price Tab Content -->
              @case ('price') {
                <div class="tab-panel">
                  @if (priceLoading) {
                    <div class="text-center py-8 text-base-content/50">Loading prices…</div>
                  } @else if (!productPrice || productPrice.series.length === 0) {
                    <div class="text-center py-8 text-base-content/50">No price data available</div>
                  } @else {
                    <!-- Current price KPIs -->
                    <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
                      @for (s of productPrice.series; track s.catalog_id; let i = $index) {
                        <div
                          class="rounded-lg border border-base-300 bg-base-100 shadow-sm p-3"
                          [style.border-left]="'3px solid ' + priceColor(i)">
                          <div class="flex items-center justify-between gap-2 mb-1">
                            <div class="flex items-center gap-1.5 min-w-0">
                              <span class="inline-block w-2 h-2 rounded-full shrink-0" [style.background-color]="priceColor(i)"></span>
                              <span class="text-xs font-medium text-base-content/60 truncate">{{ getProviderName(s.provider) }}</span>
                            </div>
                            @if (s.catalog_id === cheapestCatalogId) {
                              <ui-badge variant="success" size="sm" [outline]="true">Best price</ui-badge>
                            }
                          </div>
                          <div class="flex items-baseline gap-1.5">
                            <span class="text-2xl font-semibold">{{ s.current_price | number:'1.2-2' }}</span>
                            <span class="text-xs text-base-content/50">{{ s.currency }}</span>
                          </div>
                          @if (priceTrend(s); as trend) {
                            <div class="text-xs mt-1 font-medium" [ngClass]="trend.direction === 'up' ? 'text-error' : 'text-success'">
                              {{ trend.direction === 'up' ? '▲' : '▼' }} {{ trend.deltaPct | number:'1.1-1' }}%
                            </div>
                          }
                        </div>
                      }
                    </div>
                    <!-- Price history chart -->
                    <div echarts [options]="priceChartOptions" style="height: 280px; width: 100%;"></div>
                  }
                </div>
              }
              <!-- Insights Tab Content -->
              @case ('insights') {
                <div class="tab-panel">
                  <!-- Range filter -->
                  <div class="flex items-center justify-between flex-wrap gap-2 mb-4">
                    <div class="join border border-base-300">
                      @for (range of insightsRanges; track range.id) {
                        <button
                          type="button"
                          class="join-item btn btn-sm"
                          [ngClass]="insightsRange === range.id ? 'btn-primary' : 'btn-ghost'"
                          [attr.aria-pressed]="insightsRange === range.id"
                          (click)="onInsightsRangeChange(range.id)">{{ range.label }}</button>
                      }
                    </div>
                    @if (insightsRangeLabel && !insightsLoading) {
                      <span class="text-xs text-base-content/50">{{ insightsRangeLabel }}</span>
                    }
                  </div>

                  @if (insightsLoading) {
                    <div class="text-center py-8 text-base-content/50">Loading insights…</div>
                  } @else if (insightsFailed) {
                    <div class="flex flex-col items-center gap-3 py-8">
                      <span class="text-base-content/50">Insights couldn't be loaded.</span>
                      <button type="button" class="btn btn-sm btn-ghost" (click)="retryInsights()">Try again</button>
                    </div>
                  } @else if (!hasInsightsData) {
                    <div class="flex flex-col items-center gap-2 py-12 text-center">
                      <span class="text-sm font-medium text-base-content/60">No activity in this period</span>
                      <span class="text-xs text-base-content/50 max-w-sm">
                        Insights build up as you add stock and mark things used or thrown away.
                        Try a longer range, or check All time.
                      </span>
                    </div>
                  } @else {
                    <!-- Headline figures -->
                    <div class="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                      <div class="rounded-lg border border-base-300 bg-base-100 shadow-sm p-3">
                        <div class="flex items-center gap-1.5 mb-1">
                          <span class="inline-block w-2 h-2 rounded-sm shrink-0" [style.background-color]="consumedColor"></span>
                          <span class="text-xs font-medium text-base-content/60">Used</span>
                        </div>
                        @if (consumedAmount) {
                          <div class="flex items-baseline gap-1.5">
                            <span class="text-2xl font-semibold tabular-nums">{{ consumedAmount.value | number:'1.0-2' }}</span>
                            <span class="text-xs text-base-content/50">{{ consumedAmount.unit }}</span>
                          </div>
                        }
                        @if (consumedPerMonth) {
                          <div class="text-xs text-base-content/60 mt-1 tabular-nums">
                            ≈ {{ consumedPerMonth.value | number:'1.0-1' }} {{ consumedPerMonth.unit }} per month
                          </div>
                        }
                      </div>

                      <div class="rounded-lg border border-base-300 bg-base-100 shadow-sm p-3">
                        <div class="flex items-center gap-1.5 mb-1">
                          <span class="inline-block w-2 h-2 rounded-sm shrink-0 bg-base-content/40"></span>
                          <span class="text-xs font-medium text-base-content/60">Bought</span>
                        </div>
                        @if (addedAmount) {
                          <div class="flex items-baseline gap-1.5">
                            <span class="text-2xl font-semibold tabular-nums">{{ addedAmount.value | number:'1.0-2' }}</span>
                            <span class="text-xs text-base-content/50">{{ addedAmount.unit }}</span>
                          </div>
                        }
                        <div class="text-xs text-base-content/60 mt-1 tabular-nums">
                          {{ insightsPurchases }} {{ insightsPurchases === 1 ? 'purchase' : 'purchases' }}@if (daysBetweenPurchases) {
                            <span> · every ~{{ daysBetweenPurchases | number:'1.0-1' }} days</span>
                          }
                        </div>
                      </div>

                      <div class="rounded-lg border border-base-300 bg-base-100 shadow-sm p-3">
                        <div class="flex items-center gap-1.5 mb-1">
                          <span class="inline-block w-2 h-2 rounded-sm shrink-0" [style.background-color]="wastedColor"></span>
                          <span class="text-xs font-medium text-base-content/60">Thrown away</span>
                        </div>
                        @if (wastedAmount) {
                          <div class="flex items-baseline gap-1.5">
                            <span class="text-2xl font-semibold tabular-nums">{{ wastedAmount.value | number:'1.0-2' }}</span>
                            <span class="text-xs text-base-content/50">{{ wastedAmount.unit }}</span>
                          </div>
                        }
                        <div class="mt-1">
                          @if (wasteRate !== null && wasteRate > 0) {
                            <ui-badge variant="error" size="sm" [outline]="true">
                              {{ wasteRate | number:'1.0-1' }}% of what left stock
                            </ui-badge>
                          } @else {
                            <span class="text-xs text-base-content/60">Nothing wasted</span>
                          }
                        </div>
                      </div>

                      <div class="rounded-lg border border-base-300 bg-base-100 shadow-sm p-3">
                        <div class="flex items-center gap-1.5 mb-1">
                          <span class="inline-block w-2 h-2 rounded-sm shrink-0 bg-warning"></span>
                          <span class="text-xs font-medium text-base-content/60">Spent</span>
                        </div>
                        @if (primaryMoney) {
                          <div class="flex items-baseline gap-1.5">
                            <span class="text-2xl font-semibold tabular-nums">{{ numeric(primaryMoney.spent) | number:'1.2-2' }}</span>
                            <span class="text-xs text-base-content/50">{{ primaryMoney.currency }}</span>
                          </div>
                          <div class="mt-1">
                            <ui-badge variant="warning" size="sm" [outline]="true">
                              {{ insightsPricedPurchases }} of {{ insightsPurchases }} priced
                            </ui-badge>
                          </div>
                        } @else {
                          <div class="text-2xl font-semibold text-base-content/30">—</div>
                          <div class="text-xs text-base-content/60 mt-1">No prices recorded</div>
                        }
                      </div>
                    </div>

                    <!-- Units this product is also tracked in -->
                    @if (secondaryUnits.length > 0) {
                      <div class="text-xs text-base-content/60 mb-4">
                        Also tracked in
                        @for (unit of secondaryUnits; track unit.unit; let last = $last) {
                          <span class="tabular-nums">
                            {{ unit.unit }} — used {{ numeric(unit.consumed) | number:'1.0-2' }},
                            bought {{ numeric(unit.added) | number:'1.0-2' }}, wasted {{ numeric(unit.wasted) | number:'1.0-2' }}{{ last ? '' : ';' }}
                          </span>
                        }
                      </div>
                    }

                    <!-- Monthly trend -->
                    @if (hasInsightsTrend) {
                      <div class="rounded-lg border border-base-300 bg-base-100 shadow-sm p-4 mb-3">
                        <div class="flex items-start justify-between gap-3 flex-wrap mb-1">
                          <div>
                            <div class="text-sm font-semibold">Monthly use</div>
                            <div class="text-xs text-base-content/50">What left stock each month · {{ insightsTrendLabel }}</div>
                          </div>
                        </div>
                        <div echarts [options]="insightsTrendOptions" style="height: 280px; width: 100%;"></div>
                        <div class="text-xs text-base-content/50">
                          The current month is still filling up, so it's shown faded.
                        </div>
                      </div>
                    }

                    <!-- Money + coverage -->
                    <div class="grid grid-cols-1 lg:grid-cols-2 gap-3">
                      @if (primaryMoney) {
                        <div class="rounded-lg border border-base-300 bg-base-100 shadow-sm p-4">
                          <div class="text-sm font-semibold">Money</div>
                          <div class="text-xs text-base-content/50 mb-2">
                            {{ primaryMoney.currency }} · only purchases with a known price
                          </div>
                          <div class="flex items-baseline justify-between gap-3 py-1.5 border-b border-base-200">
                            <span class="text-sm text-base-content/70 flex items-center gap-1.5">
                              <span class="inline-block w-2 h-2 rounded-sm bg-base-content/40"></span>Bought
                            </span>
                            <span class="text-sm font-semibold tabular-nums">{{ numeric(primaryMoney.spent) | number:'1.2-2' }}</span>
                          </div>
                          <div class="flex items-baseline justify-between gap-3 py-1.5 border-b border-base-200">
                            <span class="text-sm text-base-content/70 flex items-center gap-1.5">
                              <span class="inline-block w-2 h-2 rounded-sm" [style.background-color]="consumedColor"></span>Worth of it used
                            </span>
                            <span class="text-sm font-semibold tabular-nums">{{ numeric(primaryMoney.consumed) | number:'1.2-2' }}</span>
                          </div>
                          <div class="flex items-baseline justify-between gap-3 py-1.5">
                            <span class="text-sm text-base-content/70 flex items-center gap-1.5">
                              <span class="inline-block w-2 h-2 rounded-sm" [style.background-color]="wastedColor"></span>Worth thrown away
                            </span>
                            <span class="text-sm font-semibold tabular-nums">{{ numeric(primaryMoney.wasted) | number:'1.2-2' }}</span>
                          </div>
                          @if (spendPerPurchase !== null) {
                            <div class="flex items-baseline justify-between gap-3 pt-2.5 mt-1 border-t border-base-300">
                              <span class="text-sm font-semibold">Avg per purchase</span>
                              <span class="text-base font-semibold tabular-nums">{{ spendPerPurchase | number:'1.2-2' }}</span>
                            </div>
                          }
                          @if (secondaryMoney.length > 0) {
                            <div class="text-xs text-base-content/60 mt-3">
                              @for (money of secondaryMoney; track money.currency) {
                                <div class="tabular-nums">
                                  Also {{ numeric(money.spent) | number:'1.2-2' }} {{ money.currency }} spent
                                </div>
                              }
                            </div>
                          }
                        </div>
                      }

                      @if (insightsPurchases > 0) {
                        <div class="rounded-lg border border-base-300 bg-base-100 shadow-sm p-4">
                          <div class="flex items-start justify-between gap-3">
                            <div>
                              <div class="text-sm font-semibold">Price coverage</div>
                              <div class="text-xs text-base-content/50 mb-2">One cell per purchase in this period</div>
                            </div>
                            @if (insightsCoveragePct !== null) {
                              <ui-badge [variant]="insightsCoveragePct === 100 ? 'success' : 'warning'" size="sm" [outline]="true">
                                {{ insightsCoveragePct | number:'1.0-0' }}%
                              </ui-badge>
                            }
                          </div>
                          <div class="flex flex-wrap gap-0.5 mb-2">
                            @for (priced of coverageCells; track $index) {
                              <span
                                class="block w-2.5 h-4 rounded-sm"
                                [class.bg-base-300]="!priced"
                                [style.background-color]="priced ? consumedColor : null"></span>
                            }
                          </div>
                          <p class="text-xs text-base-content/60">
                            @if (insightsPricedPurchases === insightsPurchases) {
                              Every purchase in this period has a price attached.
                            } @else {
                              {{ insightsPricedPurchases }} of {{ insightsPurchases }} purchases had a price attached, so
                              real spend is higher than shown. Prices come from imported receipts and manual entry only.
                            }
                          </p>
                        </div>
                      }
                    </div>
                  }
                </div>
              }
              <!-- Attributes Tab Content -->
              @case ('attributes') {
                <div class="tab-panel">
                  @if (attributesByProvider.length > 0) {

                    <!-- Mode toggle -->
                    <div class="flex justify-start mb-3">
                      <div class="join border border-base-300">
                        <button
                          type="button"
                          class="join-item btn btn-sm"
                          [ngClass]="compareMode ? 'btn-primary' : 'btn-ghost'"
                          (click)="compareMode = true">Compare</button>
                        <button
                          type="button"
                          class="join-item btn btn-sm"
                          [ngClass]="!compareMode ? 'btn-primary' : 'btn-ghost'"
                          (click)="compareMode = false">List</button>
                      </div>
                    </div>

                    @if (compareMode) {
                      <!-- ── Compare mode ── -->
                      <div class="border border-base-300 rounded-lg overflow-hidden">
                        <div class="overflow-x-auto">
                          <table class="table table-sm w-full">
                            <thead>
                              <tr class="bg-base-200 border-b border-base-300">
                                <th class="w-40 font-normal text-xs text-base-content/40 uppercase tracking-wide"></th>
                                @for (provider of selectedProviders; track provider; let i = $index) {
                                  <th class="relative">
                                    <div class="relative inline-block">
                                      <button
                                        type="button"
                                        [class]="availableProvidersForSlot(i).length > 0 ? 'flex items-center gap-1.5 group cursor-pointer' : 'flex items-center gap-1.5 cursor-default'"
                                        (click)="openProviderPicker(i, $event)"
                                      >
                                        <img [src]="getProviderLogoPath(provider) | async" [alt]="provider" class="h-4" />
                                        <span class="text-xs font-semibold">{{ getProviderName(provider) }}</span>
                                        @if (availableProvidersForSlot(i).length > 0) {
                                          <fa-icon [icon]="faChevronDown" class="text-[9px] text-base-content/40 group-hover:text-base-content/80 transition-colors"></fa-icon>
                                        }
                                      </button>
                                      @if (pickerOpenIndex === i) {
                                        <div
                                          class="absolute left-0 top-full mt-1 z-20 bg-base-100 border border-base-300 shadow-lg rounded-lg min-w-36 py-1"
                                          (click)="$event.stopPropagation()"
                                        >
                                          @for (alt of availableProvidersForSlot(i); track alt) {
                                            <button
                                              type="button"
                                              class="flex items-center gap-2 w-full px-3 py-1.5 text-sm hover:bg-base-200 transition-colors"
                                              (click)="swapProvider(i, alt, $event)"
                                            >
                                              <img [src]="getProviderLogoPath(alt) | async" [alt]="alt" class="h-3.5" />
                                              <span>{{ getProviderName(alt) }}</span>
                                            </button>
                                          }
                                        </div>
                                      }
                                    </div>
                                  </th>
                                }
                              </tr>
                            </thead>
                            <tbody>
                              @for (row of comparisonRows; track row.name) {
                                <tr class="hover">
                                  <td class="text-xs font-medium text-base-content/60 align-top w-40">{{ row.name | snakeToLabel }}</td>
                                  @for (provider of selectedProviders; track provider; let i = $index) {
                                    <td class="align-top text-sm transition-colors"
                                        [style]="row.cellScale.has(provider) ? cellScaleStyle(row.cellScale.get(provider)!) : null">
                                      @if (row.values[provider]; as attr) {
                                        @switch (detectAttributeFormat(attr)) {
                                          @case ('url') {
                                            <a [href]="normalizeUrl(attr.value)" target="_blank" rel="noopener noreferrer" class="link link-primary text-sm">{{ attr | productAttributeValue }}</a>
                                          }
                                          @case ('html') {
                                            <div class="prose prose-sm max-w-[260px] break-words" [innerHTML]="attr.value"></div>
                                          }
                                          @case ('number') {
                                            <span class="font-mono">{{ attr | productAttributeValue }}</span>
                                          }
                                          @case ('boolean') {
                                            <input type="checkbox" class="toggle toggle-sm toggle-primary pointer-events-none" [checked]="attr.value === 'true'" />
                                          }
                                          @default {
                                            <div class="max-w-[160px] break-words">{{ attr | productAttributeValue }}</div>
                                          }
                                        }
                                      } @else {
                                        <span class="text-base-content/25">—</span>
                                      }
                                    </td>
                                  }
                                </tr>
                              }
                            </tbody>
                          </table>
                        </div>
                      </div>

                    } @else {
                      <!-- ── List mode ── -->
                      <div class="border border-base-300 rounded-lg overflow-hidden">
                        <table class="table table-sm w-full">
                          <tbody>
                            @for (group of attributesByProvider; track group.provider; let first = $first) {
                              <tr class="bg-base-200">
                                <td colspan="3" [class.border-t]="!first" class="border-base-300 py-1.5 px-3">
                                  <div class="flex items-center gap-2">
                                    <img [src]="getProviderLogoPath(group.provider) | async" [alt]="group.provider" class="h-4 inline-block" />
                                    <span class="text-xs font-semibold uppercase tracking-wide text-base-content/60">{{ getProviderName(group.provider) }}</span>
                                  </div>
                                </td>
                              </tr>
                              @for (attr of group.attributes; track attr.name) {
                                <tr class="hover group">
                                  <th class="align-top font-medium text-sm text-base-content/60 pr-2 w-40">{{ attr.name | snakeToLabel }}</th>
                                  <td class="align-top text-sm">
                                    @switch (detectAttributeFormat(attr)) {
                                      @case ('url') {
                                        <a [href]="normalizeUrl(attr.value)" target="_blank" rel="noopener noreferrer" class="link link-primary text-sm">{{ attr | productAttributeValue }}</a>
                                      }
                                      @case ('html') {
                                        <div class="prose prose-sm max-w-none" [innerHTML]="attr.value"></div>
                                      }
                                      @case ('number') {
                                        <span class="font-mono">{{ attr | productAttributeValue }}</span>
                                      }
                                      @case ('boolean') {
                                        <input type="checkbox" class="toggle toggle-sm toggle-primary pointer-events-none" [checked]="attr.value === 'true'" />
                                      }
                                      @default {
                                        <span>{{ attr | productAttributeValue }}</span>
                                      }
                                    }
                                  </td>
                                  <td class="text-right align-middle pl-2 w-8">
                                    <button
                                      type="button"
                                      class="opacity-0 group-hover:opacity-100 transition-opacity btn btn-ghost btn-xs text-error"
                                      (click)="deleteAttribute(attr)"
                                      [uiTooltip]="'Delete attribute'">
                                      <fa-icon [icon]="faTrash"></fa-icon>
                                    </button>
                                  </td>
                                </tr>
                              }
                            }
                          </tbody>
                        </table>
                      </div>
                    }

                  } @else {
                    <div class="text-center py-8 text-base-content/50">
                      No attributes available for this product
                    </div>
                  }
                </div>
              }
            }
          </div>
        </ui-tabs>

        <!-- Barcode + Data sync — mobile only, below the tab content -->
        @if (product?.codes?.length > 0 || productSyncStatus) {
          <ui-card className="mt-6 lg:hidden overflow-hidden" [padding]="'none'" [shadow]="false" [bordered]="true">
            <ng-container [ngTemplateOutlet]="detailExtras"></ng-container>
          </ui-card>
        }
      </div>
    </div>
  </div>
} @else {
  <div class="flex justify-center items-center gap-4 p-8">
    <span class="loading loading-spinner loading-lg"></span>
    <strong>Loading...</strong>
  </div>
}

<ng-template #loading>
  <div class="flex justify-center items-center gap-4 p-8">
    <span class="loading loading-spinner loading-lg"></span>
    <strong>Loading...</strong>
  </div>
</ng-template>

<!-- Product Codes Modal -->
<dialog class="modal modal-bottom sm:modal-middle" [class.modal-open]="showCodesModal">
  <div class="modal-box max-h-[90dvh] overflow-y-auto" (click)="$event.stopPropagation()">
    <div class="flex items-center justify-between mb-5">
      <h3 class="text-lg font-bold">Product Barcodes</h3>
      <button class="btn btn-ghost btn-sm btn-circle" type="button" (click)="closeCodesModal()">✕</button>
    </div>

    <div class="space-y-3">
      @for (code of product?.codes; track code.code) {
        <div
          class="flex items-center gap-4 p-3 rounded-xl border transition-colors"
          [class.border-primary]="code.is_primary"
          [class.bg-primary/5]="code.is_primary"
          [class.border-base-300]="!code.is_primary">

          <!-- Barcode image -->
          <div class="shrink-0">
            <img
              [src]="getBarcodeUrl(code.code, 200, 50) | async"
              alt=""
              class="block"
              [style]="loadedCodes.has(code.code) ? 'width: 160px;' : 'width: 0px;'"
              (load)="onImageLoad(code.code)"
              (error)="onImageError(code.code)"/>
          </div>

          <!-- Code info -->
          <div class="flex-1 min-w-0">
            <div class="font-mono text-sm truncate">{{ code.code }}</div>
            <div class="flex items-center gap-1 mt-1 flex-wrap">
              @if (code.is_primary) {
                <span class="badge badge-primary badge-xs">Primary</span>
              }
              @if (code.type) {
                <span class="badge badge-ghost badge-xs">{{ code.type }}</span>
              }
            </div>
          </div>

          <!-- Action -->
          @if (!code.is_primary) {
            <button
              class="btn btn-ghost btn-xs shrink-0 gap-1"
              type="button"
              (click)="setPrimaryCode(code)">
              <fa-icon [icon]="faStar"></fa-icon>
              <span class="hidden sm:inline">Set primary</span>
            </button>
          } @else {
            <fa-icon [icon]="faStar" class="text-primary shrink-0 text-sm"></fa-icon>
          }
        </div>
      }
    </div>

    <div class="modal-action mt-5">
      <button class="btn" type="button" (click)="closeCodesModal()">Close</button>
    </div>
  </div>
  <div class="modal-backdrop" (click)="closeCodesModal()"></div>
</dialog>

<!-- Sync Details Modal -->
@if (productSyncStatus; as s) {
  <dialog class="modal modal-bottom sm:modal-middle" [class.modal-open]="showAllProviders">
    <div class="modal-box max-w-md" (click)="$event.stopPropagation()">
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-lg font-bold">Provider sync</h3>
        <div class="flex items-center gap-1">
          <button
            type="button"
            class="btn btn-ghost btn-sm"
            [disabled]="isSyncing"
            (click)="triggerSync($event)"
            title="Trigger sync">
            <fa-icon [icon]="faSync" [class.animate-spin]="isSyncing"></fa-icon>
          </button>
          <button class="btn btn-ghost btn-sm btn-circle" type="button" (click)="closeAllProviders($event)">✕</button>
        </div>
      </div>

      <!-- Aggregated overall status -->
      <div class="rounded-md px-3 py-2.5 flex items-center justify-between mb-3" [ngClass]="statusContextClass(s.status)">
        <div class="flex items-center gap-2">
          <fa-icon [icon]="faSync" class="text-sm" [class.animate-spin]="isSyncing"></fa-icon>
          <span class="font-semibold text-sm">Overall</span>
        </div>
        <span class="badge badge-sm font-medium" [ngClass]="statusBadgeClass(s.status)">{{ s.status }}</span>
      </div>

      <!-- Per-provider rows -->
      @for (p of sortedSyncProviders(s.providers); track p.provider) {
        <div class="py-2 border-b border-base-200 last:border-0">
          <div class="flex items-center justify-between mb-1">
            <div class="flex items-center gap-1.5">
              <img [src]="getProviderLogoPath(p.provider) | async" [alt]="p.provider" class="h-4 inline-block" />
              <span class="text-xs font-medium">{{ getProviderName(p.provider) }}</span>
            </div>
            <span class="badge badge-xs" [ngClass]="providerBadgeClass(p.status)">{{ p.status }}</span>
          </div>
          <dl class="grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs">
            <dt class="text-base-content/60">Last sync</dt>
            <dd class="text-right">{{ p.last_synced_at | date:'d. MMM H:mm' }}</dd>
            <dt class="text-base-content/60">Last success</dt>
            <dd class="text-right">{{ p.last_success_at | date:'d. MMM H:mm' }}</dd>
          </dl>
        </div>
      }
    </div>
    <div class="modal-backdrop" (click)="closeAllProviders($event)"></div>
  </dialog>
}

<!-- Product Image Dialog -->
<app-product-image-dialog
  [productId]="productId"
  (modalClosed)="onImageDialogClosed()">
</app-product-image-dialog>

